import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  ShoppingBag, DollarSign, Package, TrendingUp, AlertTriangle, Clock, Trophy, Loader2,
} from 'lucide-react';
import { formatDate } from '../../utils/formatDate';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmtMoney = (n) => `$${Number(n || 0).toLocaleString(undefined, {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
})}`;

const fmtSince = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
    return formatDate(d);
  } catch { return '—'; }
};

const ShopSummaryWidget = ({ onOpenShop }) => {
  const [data, setData] = useState(null);
  const [badge, setBadge] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [sumRes, badgeRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/shop/dashboard-summary`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/admin/shop/new-orders-badge`, { withCredentials: true }).catch(() => ({ data: { new_orders: 0 } })),
      ]);
      setData(sumRes.data);
      setBadge(badgeRes.data?.new_orders || 0);
    } catch { /* silent */ }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
    // Soft auto-refresh every 60s so newly-paid orders appear quickly
    const t = setInterval(load, 60_000);
    return () => clearInterval(t);
  }, [load]);

  const handleOpen = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/admin/shop/new-orders-badge/mark-seen`,
        {}, { withCredentials: true });
    } catch { /* silent */ }
    setBadge(0);
    if (onOpenShop) onOpenShop();
  };

  if (loading && !data) {
    return (
      <div className="bg-white border border-[#E5E7EB] rounded-lg p-4 flex justify-center" data-testid="shop-summary-loading">
        <Loader2 className="w-5 h-5 animate-spin text-[#BA1826]" />
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="bg-white border border-[#E5E7EB] rounded-lg p-4 mt-6" data-testid="shop-summary-widget">
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div className="flex items-center gap-2">
          <ShoppingBag className="w-5 h-5 text-[#BA1826]" />
          <h3 className="font-heading text-base font-bold text-[#0A0A0A]">Shop Summary</h3>
          {badge > 0 && (
            <span className="bg-[#BA1826] text-white text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full animate-pulse"
              data-testid="shop-summary-badge">
              {badge} new order{badge === 1 ? '' : 's'}
            </span>
          )}
        </div>
        <button onClick={handleOpen}
          className="text-xs px-3 py-1.5 bg-[#0A0A0A] text-white rounded hover:bg-[#333]"
          data-testid="shop-summary-open-btn">
          Open Shop Admin
        </button>
      </div>

      {/* Grid of summary tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 mb-3" data-testid="shop-summary-tiles">
        <Tile Icon={DollarSign} label="Today's revenue" value={fmtMoney(data.today_revenue)} testid="shop-tile-today-revenue" highlight={data.today_revenue > 0} />
        <Tile Icon={Package} label="Today's orders" value={String(data.today_orders || 0)} testid="shop-tile-today-orders" highlight={data.today_orders > 0} />
        <Tile Icon={Trophy} label="Best seller" value={data.best_seller?.name || '—'}
          subValue={data.best_seller ? `${data.best_seller.sold_qty} sold` : null}
          testid="shop-tile-best-seller" />
        <Tile Icon={AlertTriangle} label="Low stock" value={String(data.low_stock_count || 0)}
          subValue={data.sold_out_count > 0 ? `${data.sold_out_count} sold out` : null}
          testid="shop-tile-low-stock" warn={data.low_stock_count > 0 || data.sold_out_count > 0} />
        <Tile Icon={Clock} label="Last sale" value={fmtSince(data.last_sale_at)}
          subValue={data.last_sale ? `${data.last_sale.buyer_name || '—'} · ${fmtMoney(data.last_sale.total)}` : null}
          testid="shop-tile-last-sale" />
        <Tile Icon={TrendingUp} label="This month" value={fmtMoney(data.month_revenue)} testid="shop-tile-month-revenue" />
        <Tile Icon={DollarSign} label="All-time merch revenue" value={fmtMoney(data.all_time_revenue)} testid="shop-tile-all-time-revenue"
          className="col-span-2 lg:col-span-1" />
      </div>
    </div>
  );
};

const Tile = ({ Icon, label, value, subValue, testid, highlight = false, warn = false, className = '' }) => (
  <div className={`border rounded-lg p-2.5 ${warn ? 'bg-amber-50 border-amber-200' : highlight ? 'bg-[#FFF8F8] border-[#BA1826]/30' : 'bg-[#FAFBFC] border-[#E5E7EB]'} ${className}`}
    data-testid={testid}>
    <div className="flex items-center justify-between mb-1">
      <span className="text-[9px] uppercase tracking-wider font-bold text-[#4B5563]">{label}</span>
      <Icon className={`w-3.5 h-3.5 ${warn ? 'text-amber-700' : highlight ? 'text-[#BA1826]' : 'text-[#9CA3AF]'}`} />
    </div>
    <p className={`text-base lg:text-lg font-heading font-bold truncate ${warn ? 'text-amber-800' : 'text-[#0A0A0A]'}`} title={value}>
      {value}
    </p>
    {subValue && <p className="text-[10px] text-[#9CA3AF] truncate" title={subValue}>{subValue}</p>}
  </div>
);

export default ShopSummaryWidget;
